// src/pages/marketplace/OrderTrack.jsx
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { trackOrder } from '../../services/marketplaceService';

const STATUS_LABELS = {
  pending_approval: { label: 'Pending Approval', color: '#f59e0b', icon: '⏳' },
  approved: { label: 'Approved — Awaiting Payment', color: '#3b82f6', icon: '💳' },
  paid: { label: 'Paid — Being Prepared', color: '#8b5cf6', icon: '📦' },
  shipped: { label: 'Shipped', color: '#06b6d4', icon: '🚚' },
  completed: { label: 'Completed', color: '#10b981', icon: '✅' },
  rejected: { label: 'Rejected', color: '#ef4444', icon: '❌' },
  cancelled: { label: 'Cancelled', color: '#6b7280', icon: '🚫' },
};

export default function OrderTrack() {
  const [searchParams] = useSearchParams();
  const initialToken = searchParams.get('token') || '';

  const [token, setToken] = useState(initialToken);
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searched, setSearched] = useState(false);

  const handleTrack = async (e) => {
    e?.preventDefault();
    if (!token.trim()) return;
    setLoading(true);
    setError(null);
    setOrder(null);
    setSearched(true);

    try {
      const res = await trackOrder(token.trim());
      setOrder(res?.data?.data || null);
    } catch {
      setError('Order not found. Please check your tracking token.');
    } finally {
      setLoading(false);
    }
  };

  // Auto-search if token came from URL
  React.useEffect(() => {
    if (initialToken) handleTrack();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const statusInfo = order ? (STATUS_LABELS[order.status] || { label: order.status, color: '#888', icon: '❓' }) : null;

  return (
      <div style={{ maxWidth: 600, margin: '0 auto', padding: '3rem 1rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 style={{ fontSize: '2rem', fontWeight: 700 }}>Track Your Order</h1>
          <p style={{ color: '#888' }}>Enter your tracking token to see the current status</p>
        </div>

        <form onSubmit={handleTrack} style={{ display: 'flex', gap: '0.75rem', marginBottom: '2rem' }}>
          <input
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="Enter tracking token..."
            style={{
              flex: 1,
              padding: '0.85rem',
              borderRadius: '0.75rem',
              border: '1px solid #333',
              background: '#0f0f23',
              color: '#fff',
              fontSize: '1rem',
            }}
          />
          <button
            type="submit"
            disabled={loading}
            style={{
              padding: '0.85rem 1.5rem',
              borderRadius: '0.75rem',
              border: 'none',
              background: '#7c3aed',
              color: '#fff',
              fontWeight: 700,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            {loading ? '...' : 'Track'}
          </button>
        </form>

        {error && searched && (
          <div style={{ background: '#ef444433', color: '#ef4444', padding: '1rem', borderRadius: '0.75rem', textAlign: 'center' }}>
            {error}
          </div>
        )}

        {order && statusInfo && (
          <div style={{ background: '#1a1a2e', borderRadius: '1rem', padding: '2rem' }}>
            <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
              <div style={{ fontSize: '3rem' }}>{statusInfo.icon}</div>
              <h2 style={{ color: statusInfo.color, margin: '0.5rem 0 0', fontSize: '1.3rem' }}>{statusInfo.label}</h2>
            </div>

            <div style={{ display: 'grid', gap: '0.75rem', fontSize: '0.95rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888' }}>Order ID</span>
                <span style={{ fontFamily: 'monospace' }}>{order.order_id?.slice(0, 8)}...</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888' }}>Type</span>
                <span style={{ textTransform: 'capitalize' }}>{order.order_type}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#888' }}>Amount</span>
                <span style={{ color: '#10b981', fontWeight: 700 }}>
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(order.price)}
                </span>
              </div>
              {order.created_at && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#888' }}>Ordered</span>
                  <span>{new Date(order.created_at).toLocaleDateString()}</span>
                </div>
              )}
            </div>

            {/* Progress bar */}
            {!['rejected', 'cancelled'].includes(order.status) && (
              <div style={{ marginTop: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.7rem', color: '#666', marginBottom: '0.5rem' }}>
                  <span>Order</span><span>Approved</span><span>Paid</span><span>Shipped</span><span>Done</span>
                </div>
                <div style={{ height: 6, background: '#333', borderRadius: 3 }}>
                  <div style={{
                    height: '100%',
                    borderRadius: 3,
                    background: statusInfo.color,
                    width: {
                      pending_approval: '10%',
                      approved: '30%',
                      paid: '55%',
                      shipped: '80%',
                      completed: '100%',
                    }[order.status] || '5%',
                    transition: 'width 0.3s',
                  }} />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
  );
}
